import React, {
  useState,
  useEffect,
} from "react";
import {
  TrendingUp,
  Info,
  AlertTriangle,
  FileText,
  ChevronDown,
} from "lucide-react";
import EventProgressionPdfGenerator from "../../../utils/eventProgressionPdfGenerator";
import Workshop3Activite5CsvGenerator from "../../../utils/workshop3Activite5CsvGenerator";
import {
  showSuccessToast,
  showErrorToast,
  showLoadingToast,
  dismissToast,
} from "../../../utils/toastUtils";
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import { useTranslation } from "react-i18next";
import EventProgressionTable from "../../EventProgression/EventProgressionTable";
import api from "../../../api/apiClient";
import workshop3Service from "../../../services/workshop3Service"; // Use hydration service
import { processEventProgressionData } from "../../EventProgression/dataProcessor";
import GuideModal from "./GuideModal";
import WorkshopExportMenu from "../../common/WorkshopExportMenu";
import Button from "../../common/Button";
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';

const Activite5 = (props) => {
  const { t } = useTranslation();
  const analysisStore = useAnalysisStore();
  const atelier1Store = useAtelier1Store();
  const isWorkshopDataLoading = analysisStore.isWorkshopDataLoading;
  const currentAnalysis = analysisStore.currentAnalysis;
  const currentDreadedEvents = atelier1Store.dreadedEvents;
  const currentAnalysisControlPlan = atelier1Store.controlPlan;

  const [ecosystemMeasures, setEcosystemMeasures] = useState([]);
  const [attackPaths, setAttackPaths] = useState([]);
  const [businessValuesData, setBusinessValuesData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [progressionData, setProgressionData] = useState(null);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);

  useSilentSaveListener('atelier3-activite5', () => { console.log("Activite5 - Silent save called (informational only)"); return 'skipped'; });

  // Function to handle PDF export
  const handleExportPDF = async () => {
    const toastId = showLoadingToast("Génération du rapport PDF...");
    try {
      const generator = new EventProgressionPdfGenerator(
        {
          globalStats: progressionData.globalStats,
          eventProgression: progressionData.eventProgression,
          analysisName: currentAnalysis?.name,
          organizationName: currentAnalysis?.companyName,
          analysisId: currentAnalysis?.id,
        },
        currentAnalysis,
      );

      const blob = await generator.generateReport();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `progression-evenements-${currentAnalysis?.name || "export"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      dismissToast(toastId);
      showSuccessToast("Rapport PDF généré avec succès");
    } catch (error) {
      console.error("Error generating PDF:", error);
      dismissToast(toastId);
      showErrorToast("Erreur lors de la génération du PDF");
    }
  };

  // Function to handle English PDF export
  const handleExportPDFEn = async () => {
    const toastId = showLoadingToast("Generating PDF Report...");
    try {
      const { default: EventProgressionPdfGenerator } =
        await import("../../../utils/eventProgressionPdfGenerator.js");
      const generator = new EventProgressionPdfGenerator(
        {
          globalStats: progressionData.globalStats,
          eventProgression: progressionData.eventProgression,
          analysisName: currentAnalysis?.name,
          organizationName: currentAnalysis?.companyName,
          analysisId: currentAnalysis?.id,
        },
        currentAnalysis,
        "en"
      );

      const blob = await generator.generateReport();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `event-progression-${currentAnalysis?.name || "export"}-EN.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      dismissToast(toastId);
      showSuccessToast("PDF Report (EN) generated successfully");
    } catch (error) {
      console.error("Error generating PDF (EN):", error);
      dismissToast(toastId);
      showErrorToast("Error generating PDF (EN)");
    }
  };

  // Function to handle French CSV export
  const handleExportCsvFr = () => {
    const generator = new Workshop3Activite5CsvGenerator(progressionData, "fr");
    generator.download(
      `EBIOS-RM_Workshop3_Activite5_${currentAnalysis?.organizationName || "Organisation"}_${new Date().toISOString().split("T")[0]}.xls`,
    );
    showSuccessToast("Export CSV (FR) généré avec succès");
  };

  // Function to handle English CSV export
  const handleExportCsvEn = () => {
    const generator = new Workshop3Activite5CsvGenerator(progressionData, "en");
    generator.download(
      `EBIOS-RM_Workshop3_Activity5_${currentAnalysis?.organizationName || "Organisation"}_${new Date().toISOString().split("T")[0]}.xls`,
    );
    showSuccessToast("CSV Export (EN) generated successfully");
  };

  // Function to handle DOCX export
  const handleExportDocx = async () => {
    const toastId = showLoadingToast("Génération du rapport Word...");
    try {
      const { generateEventProgressionDocx } =
        await import("../../../utils/eventProgressionDocxGenerator");

      await generateEventProgressionDocx(
        {
          globalStats: progressionData.globalStats,
          eventProgression: progressionData.eventProgression,
          analysisName: currentAnalysis?.name,
          organizationName: currentAnalysis?.companyName,
          analysisId: currentAnalysis?.id,
        },
        currentAnalysis,
      );

      dismissToast(toastId);
      showSuccessToast("Rapport Word généré avec succès");
    } catch (error) {
      console.error("Error generating DOCX:", error);
      dismissToast(toastId);
      showErrorToast("Erreur lors de la génération du document Word");
    }
  };

  // Function to handle English DOCX export
  const handleExportDocxEn = async () => {
    const toastId = showLoadingToast("Generating Word Report...");
    try {
      const { generateEventProgressionDocx } =
        await import("../../../utils/eventProgressionDocxGenerator");

      await generateEventProgressionDocx(
        {
          globalStats: progressionData.globalStats,
          eventProgression: progressionData.eventProgression,
          analysisName: currentAnalysis?.name,
          organizationName: currentAnalysis?.companyName,
          analysisId: currentAnalysis?.id,
        },
        currentAnalysis,
        "en"
      );

      dismissToast(toastId);
      showSuccessToast("Word Report (EN) generated successfully");
    } catch (error) {
      console.error("Error generating DOCX (EN):", error);
      dismissToast(toastId);
      showErrorToast("Error generating Word (EN) document");
    }
  };

  // Fetch ecosystem measures and attack paths
  useEffect(() => {
    const fetchData = async () => {
      if (!currentAnalysis?.id) return;

      setIsLoading(true);
      try {
        // Fetch ecosystem measures from Atelier 3
        const measuresResponse = await api.get(
          `/analyses/${currentAnalysis.id}/ecosystem-measures`,
        );
        if (measuresResponse.success) {
          setEcosystemMeasures(measuresResponse.data || []);
        }

        // Fetch hydrated attack paths from Workshop 3
        console.log("🔄 Loading hydrated attack paths for PTR progression...");
        const pathsResponse = await workshop3Service.getAttackPaths(
          currentAnalysis.id,
        );
        if (pathsResponse.success && pathsResponse.data) {
          console.log(
            "✅ Loaded",
            pathsResponse.data.length,
            "hydrated attack paths for PTR",
          );
          setAttackPaths(pathsResponse.data || []);
        }

        // Fetch business values data
        const businessValuesResponse = await api.get(
          `/analyses/${currentAnalysis.id}/business-values`,
        );
        if (businessValuesResponse.success) {
          console.log(
            "Business values API response:",
            businessValuesResponse.data,
          );
          setBusinessValuesData(businessValuesResponse.data || null);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data for event progression:", error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentAnalysis]);

  // Process data to calculate progression metrics
  useEffect(() => {
    if (isLoading || isWorkshopDataLoading) return;

    const processedData = processEventProgressionData(
      currentDreadedEvents,
      currentAnalysisControlPlan,
      ecosystemMeasures,
      attackPaths,
      businessValuesData,
    );

    setProgressionData(processedData);
  }, [
    isLoading,
    isWorkshopDataLoading,
    currentDreadedEvents,
    currentAnalysisControlPlan,
    ecosystemMeasures,
    attackPaths,
    businessValuesData,
  ]);

  if (isLoading || isWorkshopDataLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">
          {t("workshop3.activity5.loading")}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center text-sm text-slate-500 mb-2">
              <span className="hover:text-blue-600 transition-colors">
                {t("workshop3.navigationTitle")}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mx-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span className="text-blue-600 font-medium">
                {t("workshop3.activity5.breadcrumb")}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center">
              <TrendingUp size={28} className="mr-3 text-blue-600" />
              {t("workshop3.activity5.title")}
            </h1>
          </div>

          <div className="flex items-center space-x-3">
            {progressionData && (
              <WorkshopExportMenu
                onExportPdfFr={handleExportPDF}
                onExportPdfEn={handleExportPDFEn}
                onExportDocxFr={handleExportDocx}
                onExportDocxEn={handleExportDocxEn}
                onExportCsvFr={handleExportCsvFr}
                onExportCsvEn={handleExportCsvEn}
              />
            )}

            {/* Help Button */}
            <Button
              variant="secondary"
              color="white"
              icon={<Info />}
              onClick={() => setIsGuideOpen(true)}
              title={t("common.help")}
            >
              {t("common.help")}
            </Button>
          </div>
        </div>
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-sm text-blue-700">
            {t("workshop3.activity5.description")}
          </p>
        </div>
      </div>

      <GuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />

      {(!currentDreadedEvents?.length ||
        !Object.keys(currentAnalysisControlPlan || {}).length) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-blue-400 mr-3" />
            <div>
              <p className="text-blue-700 font-medium">
                {t("workshop3.activity5.demoMode")}
              </p>
              <p className="text-blue-600 text-sm mt-1">
                {t("workshop3.activity5.demoModeDescription")}
              </p>
            </div>
          </div>
        </div>
      )}

      <EventProgressionTable data={progressionData} />
    </div>
  );
;
};

export default Activite5;
